import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, IconButton, Portal, useMediaQuery, useTheme } from '@mui/material';
import { Close } from '@mui/icons-material';

const DRAWER_TRANSITION = 300;

interface BottomDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Max width for desktop modal (default 440) */
  maxWidth?: number;
  children: React.ReactNode;
}

/**
 * Shared popup/drawer component.
 * Mobile (<768px): bottom drawer with drag-to-dismiss.
 * Desktop (>=768px): centered modal with blur backdrop.
 */
const BottomDrawer: React.FC<BottomDrawerProps> = ({ open, onClose, maxWidth = 440, children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startY: number; lastY: number; lastTime: number; velocity: number } | null>(null);

  useEffect(() => {
    if (open) {
      setVisible(true);
    } else {
      setAnimating(false);
      const t = setTimeout(() => setVisible(false), DRAWER_TRANSITION);
      return () => clearTimeout(t);
    }
  }, [open]);

  useLayoutEffect(() => {
    if (!visible || !open) return;
    sheetRef.current?.getBoundingClientRect();
    const raf = requestAnimationFrame(() => {
      setAnimating(true);
    });
    return () => cancelAnimationFrame(raf);
  }, [visible, open]);

  // Lock body scroll while open
  useEffect(() => {
    if (!animating) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [animating]);

  const animateClose = useCallback((dy: number) => {
    setAnimating(false);
    const sheet = sheetRef.current;
    const backdrop = backdropRef.current;
    if (!sheet) { setVisible(false); onClose(); return; }
    if (isMobile) {
      const remaining = sheet.offsetHeight - dy;
      const duration = Math.max(80, Math.min(DRAWER_TRANSITION, remaining * (DRAWER_TRANSITION / sheet.offsetHeight)));
      sheet.style.transition = `transform ${duration}ms cubic-bezier(0.2, 0, 0.5, 1)`;
      sheet.style.transform = `translateY(${sheet.offsetHeight}px)`;
    } else {
      sheet.style.transition = `opacity ${DRAWER_TRANSITION}ms ease`;
      sheet.style.opacity = '0';
    }
    if (backdrop) {
      backdrop.style.transition = `opacity ${DRAWER_TRANSITION}ms ease`;
      backdrop.style.opacity = '0';
    }
    sheet.addEventListener('transitionend', () => {
      setVisible(false);
      onClose();
    }, { once: true });
  }, [onClose, isMobile]);

  const finishDrag = useCallback((dy: number, velocity: number) => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    const shouldDismiss = dy > 80 || velocity > 0.5;
    const backdrop = backdropRef.current;
    if (shouldDismiss) {
      setAnimating(false);
      const speed = Math.max(0.8, Math.min(4, Math.abs(velocity)));
      const remaining = sheet.offsetHeight - dy;
      const duration = Math.max(80, Math.min(400, remaining / speed));
      sheet.style.transition = `transform ${duration}ms cubic-bezier(0.2, 0, 0.5, 1)`;
      sheet.style.transform = `translateY(${sheet.offsetHeight}px)`;
      if (backdrop) {
        backdrop.style.transition = `opacity ${duration}ms ease`;
        backdrop.style.opacity = '0';
      }
      sheet.addEventListener('transitionend', () => {
        setVisible(false);
        onClose();
      }, { once: true });
    } else {
      sheet.style.transition = `transform 250ms cubic-bezier(0.2, 0, 0, 1)`;
      sheet.style.transform = '';
      if (backdrop) {
        backdrop.style.transition = `opacity 250ms ease`;
        backdrop.style.opacity = '';
      }
      sheet.addEventListener('transitionend', () => {
        sheet.style.transition = '';
        if (backdrop) { backdrop.style.transition = ''; }
      }, { once: true });
    }
  }, [onClose]);

  const trackMove = useCallback((clientY: number) => {
    if (!dragRef.current || !sheetRef.current) return;
    const now = performance.now();
    const dt = now - dragRef.current.lastTime;
    if (dt > 0) {
      const instantV = (clientY - dragRef.current.lastY) / dt;
      dragRef.current.velocity = dragRef.current.velocity * 0.3 + instantV * 0.7;
    }
    dragRef.current.lastY = clientY;
    dragRef.current.lastTime = now;
    const dy = Math.max(0, clientY - dragRef.current.startY);
    sheetRef.current.style.transform = `translateY(${dy}px)`;
    if (backdropRef.current && sheetRef.current) {
      const progress = Math.max(0, 1 - dy / sheetRef.current.offsetHeight);
      backdropRef.current.style.opacity = progress.toFixed(3);
    }
  }, []);

  const dismiss = useCallback(() => {
    animateClose(0);
  }, [animateClose]);

  // Touch drag-to-dismiss (mobile only)
  // Initiates drag from anywhere in the drawer except interactive maps.
  // If content is scrolled down, scrolling takes priority; drag only starts when at scroll top.
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!isMobile) return;
    const target = e.target as HTMLElement;

    // Don't drag from interactive map areas (Leaflet)
    if (target.closest('.leaflet-container')) return;

    const sheet = sheetRef.current;
    if (!sheet) return;

    const startY = e.touches[0].clientY;
    const isHandle = !!target.closest('[data-drawer-handle]');

    // Track whether this touch became a drag
    let isDragging = false;
    let moved = false;

    const startDrag = () => {
      isDragging = true;
      dragRef.current = { startY, lastY: startY, lastTime: performance.now(), velocity: 0 };
      if (sheet) sheet.style.transition = 'none';
      if (backdropRef.current) backdropRef.current.style.transition = 'none';
    };

    // From handle, always start drag immediately
    if (isHandle) {
      startDrag();
    }

    const handleTouchMove = (ev: TouchEvent) => {
      const clientY = ev.touches[0].clientY;
      const dy = clientY - startY;

      if (!isDragging) {
        // Only start dragging if swiping down while at scroll top
        if (dy > 0 && sheet.scrollTop <= 0 && !moved) {
          startDrag();
        } else {
          moved = true;
          return; // Let native scroll handle it
        }
      }

      if (isDragging) {
        ev.preventDefault();
        trackMove(clientY);
      }
    };

    const handleTouchEnd = (ev: TouchEvent) => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      if (!isDragging || !dragRef.current) return;
      const clientY = ev.changedTouches[0].clientY;
      const dy = clientY - dragRef.current.startY;
      const velocity = dragRef.current.velocity;
      dragRef.current = null;
      finishDrag(dy, velocity);
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  }, [isMobile, trackMove, finishDrag]);

  if (!visible) return null;

  return (
    <Portal>
      {/* Backdrop — stops above bottom nav on mobile so nav stays interactive */}
      <Box
        ref={backdropRef}
        onClick={dismiss}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: { xs: 56, lg: 0 },
          bgcolor: 'rgba(0,0,0,0.32)',
          zIndex: { xs: 1099, lg: 1300 },
          opacity: animating ? 1 : 0,
          transition: `opacity ${DRAWER_TRANSITION}ms ease`,
        }}
      />

      {isMobile ? (
        /* Mobile: Bottom drawer — slides behind bottom nav */
        <Box
          ref={sheetRef}
          onTouchStart={handleTouchStart}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1099,
            bgcolor: 'background.paper',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
            transform: animating ? 'translateY(0)' : 'translateY(100%)',
            transition: `transform ${DRAWER_TRANSITION}ms cubic-bezier(0.32, 0.72, 0, 1)`,
            userSelect: 'none',
            WebkitUserSelect: 'none',
            maxHeight: '80vh',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            pb: `calc(56px + env(safe-area-inset-bottom, 0px) + 16px)`,
          }}
        >
          {/* Drawer handle — sticky so it stays visible when content scrolls */}
          <Box data-drawer-handle sx={{ position: 'sticky', top: 0, zIndex: 1, display: 'flex', justifyContent: 'center', pt: '10px', pb: '6px', touchAction: 'none', bgcolor: 'background.paper', borderTopLeftRadius: 16, borderTopRightRadius: 16 }}>
            <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'action.disabled' }} />
          </Box>
          {children}
        </Box>
      ) : (
        /* Desktop: Centered modal — use flexbox centering (not transform)
           so Leaflet maps inside don't break from transform stacking context */
        <Box
          onClick={dismiss}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1301,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Box
            ref={sheetRef}
            onClick={(e) => e.stopPropagation()}
            sx={{
              pointerEvents: 'auto',
              position: 'relative',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '16px',
              boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
              maxHeight: '85vh',
              overflowY: 'auto',
              width: `min(${maxWidth}px, calc(100% - 32px))`,
              opacity: animating ? 1 : 0,
              transition: `opacity ${DRAWER_TRANSITION}ms ease`,
            }}
          >
            <IconButton
              aria-label="close"
              onClick={dismiss}
              size="small"
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                zIndex: 1,
                color: 'text.secondary',
              }}
            >
              <Close fontSize="small" />
            </IconButton>
            {children}
          </Box>
        </Box>
      )}
    </Portal>
  );
};

export default BottomDrawer;
